// Central API client for communicating with the backend
// Uses fetch() but could be replaced with axios if preferred

// In this repo, the frontend runs directly in the browser (CDN + Babel),
// so provide a couple of simple override options:
// - window.__API_BASE_URL__ = "http://localhost:5000/api/v1"
// - localStorage.setItem("rd_api_base_url", "http://localhost:5000/api/v1")
const BASE_URL =
  (typeof window !== "undefined" && window.__API_BASE_URL__) ||
  (typeof localStorage !== "undefined" && localStorage.getItem("rd_api_base_url")) ||
  (typeof window !== "undefined" ? window.location.protocol + '//' + window.location.hostname + (window.location.port ? ':' + window.location.port : '') + '/api/v1' : "http://localhost:5000/api/v1");

let authToken = null;

const setAuthToken = token => {
  authToken = token;
  if (token) {
    localStorage.setItem("rd_token", token);
  } else {
    localStorage.removeItem("rd_token");
  }
};

const getAuthToken = () => {
  if (!authToken) {
    authToken = localStorage.getItem("rd_token");
  }
  return authToken;
};

async function request(path, options = {}) {
  const headers = options.headers || {};
  const token = getAuthToken();
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  headers["Content-Type"] = "application/json";

  const res = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers,
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) {
    const error = new Error(payload.message || "API request failed");
    error.response = payload;
    throw error;
  }
  return payload;
}

// Authentication
const apiRegister = data => request("/auth/register", { method: "POST", body: JSON.stringify(data) });
const apiLogin = data => request("/auth/login", { method: "POST", body: JSON.stringify(data) });
const apiLogout = () => { setAuthToken(null); };
const apiRequestOtp = data => request("/auth/request-otp", { method: "POST", body: JSON.stringify(data) });
const apiVerifyOtp = data => request("/auth/verify-otp", { method: "POST", body: JSON.stringify(data) });

// Properties
const apiGetProperties = (filters = {}, page = 1, limit = 20) => {
  const params = new URLSearchParams({ page, limit, ...filters });
  return request(`/properties?${params.toString()}`);
};
const apiGetProperty = id => request(`/properties/${id}`);
const apiCreateProperty = data => request(`/properties`, { method: "POST", body: JSON.stringify(data) });
const apiUpdateProperty = (id, data) => request(`/properties/${id}`, { method: "PUT", body: JSON.stringify(data) });
const apiDeleteProperty = id => request(`/properties/${id}`, { method: "DELETE" });

// Media / Amenities / Availability run through property endpoints
// Bookings
const apiCreateBooking = data => request(`/bookings`, { method: "POST", body: JSON.stringify(data) });
const apiGetBookings = (role = "client", page = 1, limit = 20) => {
  // Backend routes:
  // - GET /bookings/my-bookings (CLIENT)
  // - GET /bookings/agent/requests (AGENT)
  const normalized = String(role || "").toLowerCase();
  const isAgent = normalized === "agent";
  const endpoint = isAgent ? "/bookings/agent/requests" : "/bookings/my-bookings";
  const params = new URLSearchParams({ page, limit });
  return request(`${endpoint}?${params.toString()}`);
};

// Favorites
const apiToggleFavorite = propertyId => request(`/favorites`, { method: "POST", body: JSON.stringify({ propertyId }) });
const apiGetFavorites = (page = 1, limit = 50) => {
  const params = new URLSearchParams({ page, limit });
  return request(`/favorites?${params.toString()}`);
};

// Reviews
const apiCreateReview = data => request(`/reviews`, { method: "POST", body: JSON.stringify(data) });
const apiGetReviewsForProperty = async (propertyId) => {
  // Property details include embedded reviews.
  const res = await request(`/properties/${propertyId}`);
  return {
    success: !!res?.success,
    message: res?.message || "OK",
    data: res?.data?.reviews || [],
  };
};

// Messaging
const apiStartConversation = data => request(`/conversations`, { method: "POST", body: JSON.stringify(data) });
const apiStartConversationAsAgent = data => request(`/conversations/agent`, { method: "POST", body: JSON.stringify(data) });
const apiGetConversations = (page = 1, limit = 20) => {
  const params = new URLSearchParams({ page, limit });
  return request(`/conversations?${params.toString()}`);
};
const apiGetConversation = (conversationId, page = 1, limit = 50) => {
  // Alias: backend exposes messages under /conversations/:conversationId/messages
  const params = new URLSearchParams({ page, limit });
  return request(`/conversations/${conversationId}/messages?${params.toString()}`);
};
const apiSendMessage = (conversationIdOrData, maybeData) => {
  // Supports:
  // - apiSendMessage(conversationId, { message })
  // - apiSendMessage({ conversationId, message })
  const conversationId =
    typeof conversationIdOrData === "string" ? conversationIdOrData : conversationIdOrData?.conversationId;
  const data =
    typeof conversationIdOrData === "string" ? maybeData : { message: conversationIdOrData?.message };

  if (!conversationId) {
    return Promise.reject(new Error("conversationId is required"));
  }

  return request(`/conversations/${conversationId}/messages`, {
    method: "POST",
    body: JSON.stringify(data),
  });
};

// Notifications
const apiGetNotifications = () => request(`/notifications`);

// Billing / Subscription (Agents/Realtors)
const apiSubscribe = (planCode) =>
  request(`/billing/subscribe`, { method: "POST", body: JSON.stringify({ planCode }) });
const apiGetMySubscription = () => request(`/billing/subscription`);

// Availability
const apiGetAvailability = (propertyId) => request(`/properties/${propertyId}/availability`);
const apiCheckAvailability = (propertyId, data) =>
  request(`/properties/${propertyId}/availability/check`, { method: "POST", body: JSON.stringify(data) });
const apiUpdateAvailability = (propertyId, slots) =>
  request(`/properties/${propertyId}/availability`, { method: "PUT", body: JSON.stringify({ slots }) });

// Additional helpers can be added as needed

const _api = {
  request,
  setAuthToken,
  getAuthToken,
  apiRegister,
  apiLogin,
  apiLogout,
  apiRequestOtp,
  apiVerifyOtp,
  apiGetProperties,
  apiGetProperty,
  apiCreateProperty,
  apiUpdateProperty,
  apiDeleteProperty,
  apiCreateBooking,
  apiGetBookings,
  apiToggleFavorite,
  apiGetFavorites,
  apiCreateReview,
  apiGetReviewsForProperty,
  apiSendMessage,
  apiGetConversation,
  apiGetConversations,
  apiStartConversation,
  apiStartConversationAsAgent,
  apiGetNotifications,
  apiSubscribe,
  apiGetMySubscription,
  apiGetAvailability,
  apiCheckAvailability,
  apiUpdateAvailability,
};

// expose to window for global scripts
window.api = _api;
